import { withErrorBoundary } from 'react-error-boundary';
import styled, { css } from 'styled-components';
import Card from 'components/generic/card';
import { default as GenericImage } from 'components/generic/Image';
import { Typography } from 'components/generic/typography';
import { focusVisibleOutline, verticalEllipsis } from 'modules/helpers/mixins';
import {
    BorderRadiusMedium,
    ColorNeutralWhite,
    Spacing12,
    Spacing16,
    Spacing4,
    Spacing8
} from 'autogen/design-tokens/tokens';
import type { PageActionModel } from 'components/shared/Routing/PageAction';
import type { HTMLAttributes } from 'react';
import Tag from 'components/generic/tag';
import { Button } from 'components/Button';
import InlineButton from 'components/generic/InlineButton';
import type { ButtonVariant } from 'components/Button/shared/buttonVariants';
import PageAction from 'components/shared/Routing/PageAction';

type Action = {
    label: string;
    variant?: ButtonVariant;
    onClick: () => void;
};

export type ContentCardProps = {
    image?: {
        src: string;
        srcSet?: string;
        sizes?: string;
        alt: string;
        // Height defualts to 200px. 100% makes the image auto scale and take up remaning space.
        height?: string;
    };
    title?: string;
    topics?: string[];
    link: {
        label?: string;
        action: PageActionModel;
    };
    actions?: Action[];
    section?: string;
    backgroundColor?: string;
} & HTMLAttributes<HTMLElement>;

// #region Styles

const StyledPageAction = styled(PageAction)`
    text-decoration: none;
    
    ${focusVisibleOutline}
    &:focus-visible {
        border-radius: ${BorderRadiusMedium};
    }

    &:focus,
    &:hover {
        text-decoration: none;
    }
`;
const StyledCard = styled(Card)`
    position: relative;
    height: 100%;
    background: none; // This fixes border radius issue where the white background could be see at the edge of images.
`;

const StyledCardContent = styled(Card.Content)<{ $backgroundColor: ContentCardProps['backgroundColor'] }>`
    ${({ $backgroundColor }) => css`background-color: ${$backgroundColor || ColorNeutralWhite};`}
`;

const ImageWrapper = styled.div<{ $height: NonNullable<ContentCardProps['image']>['height'] }>`
    position: relative;
    ${({ $height }) => css`height: ${$height ? $height : '200px'};`}
`;

const StyledGenericImage = styled(GenericImage)`
    position: absolute;
    object-fit: cover;
    object-position: center center; 
    height: 100%;
    width: 100%;
    min-height: 0;
`;

const SectionText = styled(Typography.Label)`
    margin-bottom: ${Spacing4};
`;

const TitleText = styled(Typography.Headline)`
    ${verticalEllipsis(3)}
`;

const StyledTag = styled(Tag)`
    background-color: ${ColorNeutralWhite};
    margin-bottom: ${Spacing4};
`;

const TopicContainer = styled.div`
    position: absolute;
    top: ${Spacing12};
    left: ${Spacing12};
    right: ${Spacing12};
    display: flex;
    flex-direction: column;
    align-items: start;
`;

const ChildrenContainer = styled.div`
    margin-block: ${Spacing8};
`;

const LinkText = styled(InlineButton)`
`;

const ActionsAndLinkContainer = styled.div<{ $justifyLeft: boolean }>`
    margin-top: auto;
    padding-top:  ${Spacing16};
    display: grid;
    grid-template-columns: 1fr 1fr;
    align-items: center;
    gap: ${Spacing12};

    ${({ $justifyLeft }) => $justifyLeft && css`justify-items: left;`}
`;
// #endregion Styles

function renderTopics(topics?: string[]) {
    if (!topics?.length) return null;

    return (
        <TopicContainer>
            {topics.map((topic, index) => (
                <StyledTag key={index}>{topic}</StyledTag>
            ))}
        </TopicContainer>
    );
}

function renderLinkAndActions(link: ContentCardProps['link'], actions?: ContentCardProps['actions']) {
    if (!link) return null;
    if (!link.label && !actions?.length) return null;

    return (
        <ActionsAndLinkContainer $justifyLeft={!actions?.length}>
            {actions?.map((action, index) => (
                <Button key={index} {...action} size="sm">
                    {action.label}
                </Button>
            ))}
            {link.label ? (
                <LinkText forwardedAs="span" variant="dark" size="M" fontWeight="semibold">
                    {link.label}
                </LinkText>
            ) : null}
        </ActionsAndLinkContainer>
    );
}

function ContentCard(props: ContentCardProps) {
    const { title, image, section, topics, link, actions, backgroundColor, ...restProps } = props;
    const { children } = restProps;

    return (
        <StyledPageAction action={link.action}>
            <StyledCard {...restProps}>
                {image ? (
                    <ImageWrapper $height={image.height}>
                        <StyledGenericImage {...image} />
                    </ImageWrapper>
                ) : null}
                {renderTopics(topics)}
                <StyledCardContent $backgroundColor={backgroundColor}>
                    <SectionText size="S">{section}</SectionText>
                    <TitleText size="S">{title}</TitleText>
                    {children && <ChildrenContainer>{children}</ChildrenContainer>}
                    {renderLinkAndActions(link, actions)}
                </StyledCardContent>
            </StyledCard>
        </StyledPageAction>
    );
}

export default withErrorBoundary(ContentCard, {
    fallback: null,
    onError(error, info) {
        console.error(error, info);
    }
});
